/**
 * Attachment upload + batch processing API.
 *
 * Upload flow:
 *   1. createAttachment() — POST to backend, get presigned S3 URL
 *   2. uploadToPresignedUrl() — PUT file bytes directly to S3
 *
 * Processing flow (after all uploads complete):
 *   3. startBatchProcessing() — POST attachment IDs, get batch_id
 *   4a. streamBatchStatus() — SSE stream (preferred)
 *   4b. getBatchStatus()    — polling fallback when SSE returns 503
 */
import axios from "axios";
import { fetchEventSource, EventStreamContentType } from "@microsoft/fetch-event-source";
import api from "@/services/api";
import type {
  Attachment,
  CreateAttachmentResponse,
  StartBatchResponse,
  BatchStatusResponse,
  ActiveBatchesResponse,
  AttachmentListResponse,
  BatchChildStatus,
  BatchStartedPayload,
  BatchLifecyclePayload,
  BatchChildResultPayload,
  BatchCompletePayload,
  BatchEventType,
} from "../types/attachment";
import { BATCH_EVENT_TYPE } from "../types/attachment";

const VIP_BASE_URL = "/vip/v2";
const ATTACHMENT_BASE_URL = "/fulfilment/accounts-payable/api/v2/attachments";

/**
 * Step 1: Create an attachment record and receive a presigned S3 upload URL.
 * Uses the api singleton (needs gateway URL + auth headers).
 */
export async function createAttachment(name: string): Promise<CreateAttachmentResponse> {
  return api.post(ATTACHMENT_BASE_URL, { name });
}

/**
 * Step 2: Upload file bytes directly to the presigned S3 URL.
 *
 * Uses raw axios (not the api singleton) because:
 * - Presigned URL is a direct S3 endpoint — no auth headers, no gateway prefix
 * - Same pattern as qcService.uploadImagesToAWS
 *
 * Returns an AbortController so callers can cancel in-flight uploads.
 */
export function uploadToPresignedUrl(
  url: string,
  file: File,
  onProgress: (percent: number) => void,
): { promise: Promise<void>; controller: AbortController } {
  const controller = new AbortController();

  const promise = axios
    .put(url, file, {
      headers: { "Content-Type": file.type || "application/octet-stream" },
      signal: controller.signal,
      onUploadProgress: (event) => {
        if (event.total) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      },
    })
    .then(() => undefined); // discard axios response — we only need success/failure

  return { promise, controller };
}

// --- Attachment list ---

/**
 * Fetch all attachments for an invoice.
 * GET /vip/v2/INVOICE/:invoiceId/attachments
 */
export async function getInvoiceAttachments(invoiceId: number): Promise<AttachmentListResponse> {
  return api.get(`${VIP_BASE_URL}/INVOICE/${invoiceId}/attachments`);
}

/**
 * Fetch metadata (name, url) for multiple attachments by ID.
 * GET /fulfilment/accounts-payable/api/v2/attachments?ids=id1,id2
 */
export async function getAttachmentsByIds(ids: string[]): Promise<{ attachments: Attachment[] }> {
  return api.get(`${ATTACHMENT_BASE_URL}?ids=${ids.join(",")}`);
}

// --- Batch processing (OCR) ---

const VIP_INVOICES_BASE_URL = "/fulfilment/accounts-payable/api/v2/vip/invoices";
const BATCH_BASE_URL = `${VIP_INVOICES_BASE_URL}/process-batch`;
const REPROCESS_URL = `${VIP_INVOICES_BASE_URL}/reprocess`;

/**
 * Fetch all recent batches.
 * Used on mount to restore in-progress batches after a page refresh.
 */
export async function getActiveBatches(): Promise<ActiveBatchesResponse> {
  return api.get(BATCH_BASE_URL);
}

/** Optional PO / invoice context for the batch-processing request. */
export type BatchProcessContext = {
  poCode?: string;
  invoiceId?: number;
};

/**
 * Step 3: Trigger batch processing for uploaded attachments.
 * Called once after all S3 uploads complete, with all successful attachment IDs.
 * Returns a workflow_id used for polling.
 *
 * When context (poCode / invoiceId) is provided the new per-attachment form is
 * used so the backend can associate OCR results with the right PO / invoice.
 * The legacy attachment_ids[] form is used when no context is passed.
 */
export async function startBatchProcessing(
  attachmentIds: string[],
  context?: BatchProcessContext,
): Promise<StartBatchResponse> {
  if (context?.poCode || context?.invoiceId) {
    const attachments = attachmentIds.map((id) => ({
      attachment_id: id,
      ...(context.poCode && { po_code: context.poCode }),
      ...(context.invoiceId && { invoice_id: context.invoiceId }),
    }));
    return api.post(BATCH_BASE_URL, { attachments });
  }
  return api.post(BATCH_BASE_URL, { attachment_ids: attachmentIds });
}

/**
 * Re-run OCR + validation + matching on an existing v2 invoice, in place.
 * Starts a one-attachment batch; poll it with getBatchStatus. A failed re-read
 * leaves the invoice unchanged (child status OCR_FAILED).
 */
export async function reprocessInvoice(
  invoiceId: number,
  attachmentId: string,
): Promise<StartBatchResponse> {
  return api.post(REPROCESS_URL, {
    invoice_id: invoiceId,
    attachment_id: attachmentId,
  });
}

/**
 * Poll batch processing status.
 * Returns per-child OCR status and a `done` flag.
 * 404 means the workflow aged out of Temporal retention — treat as done.
 */
export async function getBatchStatus(
  batchId: string,
): Promise<BatchStatusResponse> {
  const res: BatchStatusResponse = await api.get(`${BATCH_BASE_URL}/${batchId}/status`);
  // The status endpoint names the failure `error`; SSE child_result names it `reason`.
  return { ...res, children: res?.children?.map((c) => ({ ...c, reason: c.reason ?? c.error })) };
}

// --- SSE streaming ---

export type StreamBatchHandlers = {
  onTotalFiles?: (total: number) => void;
  onChildResult: (child: BatchChildStatus) => void;
  /** Called on each lifecycle transition — fires multiple times per invoice as it moves through statuses */
  onLifecycleTransition?: (payload: BatchLifecyclePayload) => void;
  onComplete: (successCount: number, failureCount: number) => void;
  /** Called when BE returns 503 — caller should fall back to polling */
  onFallback: () => void;
  /** Called on unrecoverable errors (404 batch expired, 410 gone) */
  onFatalError: (status: number) => void;
};

/**
 * Open an SSE stream for batch processing status.
 *
 * - Reconnects automatically on network drops (library handles backoff)
 * - Replays full stream from offset 0 on reconnect — callers use latest-wins keying
 * - 503: calls onFallback and stops (caller switches to polling)
 * - 404/410: calls onFatalError and stops (batch expired)
 *
 * Returns an AbortController — call .abort() to close the stream (e.g. on unmount).
 */
export function streamBatchStatus(
  batchId: string,
  token: string,
  handlers: StreamBatchHandlers,
): AbortController {
  const controller = new AbortController();
  const gateway: string = (window as any).config?.REACT_APP_APIGATEWAY ?? "";
  const url = `${gateway}${BATCH_BASE_URL}/${batchId}/stream`;

  fetchEventSource(url, {
    signal: controller.signal,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: EventStreamContentType,
    },
    openWhenHidden: true,

    async onopen(response) {
      if (response.ok && response.headers.get("content-type")?.includes(EventStreamContentType)) {
        return; // connected successfully
      }

      // Surface a structured error so onerror can inspect the status code
      const err = new Error(`SSE_${response.status}`) as Error & { status: number };
      err.status = response.status;
      throw err;
    },

    onmessage(event) {
      const type = event.event as BatchEventType;
      if (!event.data) return;

      try {
        const payload = JSON.parse(event.data);

        if (type === BATCH_EVENT_TYPE.BATCH_STARTED) {
          const data = payload as BatchStartedPayload;
          handlers.onTotalFiles?.(data.total);
        } else if (type === BATCH_EVENT_TYPE.LIFECYCLE) {
          const data = payload as BatchLifecyclePayload;
          handlers.onLifecycleTransition?.(data);
        } else if (type === BATCH_EVENT_TYPE.CHILD_RESULT) {
          const data = payload as BatchChildResultPayload;
          handlers.onChildResult({
            attachment_id: data.attachment_id,
            invoice_id: data.invoice_id,
            status: data.status,
            reason: data.reason,
          });
        } else if (type === BATCH_EVENT_TYPE.BATCH_COMPLETE) {
          const data = payload as BatchCompletePayload;
          handlers.onComplete(data.succeeded, data.failed);
          controller.abort(); // stream is done — close gracefully
        }
        // BATCH_EVENT_TYPE.LIFECYCLE is informational — no UI action needed
      } catch {
        // Ignore unparseable events
      }
    },

    onerror(err: any) {
      const status: number | undefined = err?.status;

      if (status === 401) {
        // Token expired — fall back to polling (axios interceptor handles token refresh)
        handlers.onFallback();
        throw err; // stop retrying
      }

      if (status === 503) {
        // SSE not available — fall back to polling
        handlers.onFallback();
        throw err; // stop retrying
      }

      if (status === 404 || status === 410) {
        // Batch expired or not found — terminal state
        handlers.onFatalError(status);
        throw err; // stop retrying
      }

      // Other errors (network drops) — return undefined to let library reconnect
    },
  });

  return controller;
}
